/**
 * Ref-counted app-wide busy tracker.
 * Used by API interceptors and action buttons so concurrent work does not flicker off early.
 */

type Listener = (busy: boolean, count: number) => void;

const listeners = new Set<Listener>();
let count = 0;

function emit() {
  const busy = count > 0;
  listeners.forEach((listener) => listener(busy, count));
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("loading", { detail: busy }));
    window.dispatchEvent(new CustomEvent("app-busy", { detail: { busy, count } }));
  }
}

export const globalBusyTracker = {
  begin() {
    count += 1;
    emit();
  },

  end() {
    count = Math.max(0, count - 1);
    emit();
  },

  get busy() {
    return count > 0;
  },

  get count() {
    return count;
  },

  subscribe(listener: Listener) {
    listeners.add(listener);
    listener(count > 0, count);
    return () => {
      listeners.delete(listener);
    };
  },

  /** Resolves when the ref-count returns to zero. */
  whenIdle(): Promise<void> {
    if (count === 0) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const unsubscribe = globalBusyTracker.subscribe((busy) => {
        if (!busy) {
          unsubscribe();
          resolve();
        }
      });
    });
  },
};

function isThenable(value: unknown): value is PromiseLike<unknown> {
  return (
    value != null &&
    (typeof value === "object" || typeof value === "function") &&
    typeof (value as PromiseLike<unknown>).then === "function"
  );
}

/**
 * After a click handler runs, wait until:
 * - its returned promise settles (if any), and
 * - any API requests started by that handler have finished.
 *
 * Covers fire-and-forget handlers (`void doWork()`) via the API busy counter.
 */
export async function settleAction(actionResult: unknown, graceMs = 120): Promise<void> {
  if (isThenable(actionResult)) {
    try {
      await actionResult;
    } catch {
      // Caller / global error handlers own the failure UX.
    }
  }

  const deadline = Date.now() + graceMs;
  while (Date.now() < deadline) {
    if (globalBusyTracker.busy) {
      await globalBusyTracker.whenIdle();
      return;
    }
    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, 10);
    });
  }

  if (globalBusyTracker.busy) {
    await globalBusyTracker.whenIdle();
  }
}
